import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Package, Plus, Trash2, Edit2, Wrench, ShoppingCart, RotateCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  item_name: '',
  category: 'purchase',
  quantity: 1,
  estimated_cost: '',
  priority: 'medium',
  reason: '',
  location: '',
  notes: '',
};

const priorityBadge = (p) => {
  const map = {
    urgent: 'bg-[#FEE2E2] text-[#EF4444] border-[#EF4444]',
    high: 'bg-[#FEF3C7] text-[#F59E0B] border-[#F59E0B]',
    medium: 'bg-blue-100 text-blue-700 border-blue-300',
    low: 'bg-slate-100 text-slate-700 border-slate-300',
  };
  return <Badge className={`${map[p] || map.medium} border`}>{p}</Badge>;
};

const categoryIcon = (c) => {
  if (c === 'purchase') return <ShoppingCart className="w-4 h-4 text-blue-600" />;
  if (c === 'restock') return <RotateCw className="w-4 h-4 text-emerald-600" />;
  return <Wrench className="w-4 h-4 text-orange-600" />;
};

const statusBadge = (s) => {
  const map = {
    pending: 'bg-[#FEF3C7] text-[#F59E0B] border-[#F59E0B]',
    purchased: 'bg-[#D1FAE5] text-[#10B981] border-[#10B981]',
    completed: 'bg-[#D1FAE5] text-[#10B981] border-[#10B981]',
    cancelled: 'bg-[#FEE2E2] text-[#EF4444] border-[#EF4444]',
  };
  return <Badge className={`${map[s] || map.pending} border`}>{s || 'pending'}</Badge>;
};

const PurchaseItemsSection = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/purchase-items`, { withCredentials: true });
      setItems(data);
    } catch (e) { toast.error('Failed to load items'); } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...emptyForm, ...it, estimated_cost: it.estimated_cost ?? '' }); setDialogOpen(true); };

  const saveItem = async () => {
    if (!form.item_name || !form.category) {
      toast.error('Item name and category are required');
      return;
    }
    try {
      const payload = {
        ...form,
        quantity: parseInt(form.quantity) || 1,
        estimated_cost: form.estimated_cost === '' ? null : parseFloat(form.estimated_cost),
      };
      if (editing) {
        await axios.put(`${API}/api/purchase-items/${editing.id}`, payload, { withCredentials: true });
        toast.success('Item updated');
      } else {
        await axios.post(`${API}/api/purchase-items`, payload, { withCredentials: true });
        toast.success('Item added');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const updateStatus = async (id, admin_status) => {
    try {
      await axios.put(`${API}/api/purchase-items/${id}`, { admin_status }, { withCredentials: true });
      toast.success('Status updated');
      fetchAll();
    } catch (e) { toast.error('Failed to update status'); }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await axios.delete(`${API}/api/purchase-items/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchAll();
    } catch (e) { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <Card className="bg-white border-slate-200" data-testid="purchase-items-section">
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <div>
          <CardTitle className="text-xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Purchase • Restock • Repair</CardTitle>
          <CardDescription>{isAdmin ? 'All items submitted by teachers' : 'List items you need purchased, restocked, or repaired'}</CardDescription>
        </div>
        {!isAdmin && (
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={openCreate} data-testid="new-purchase-item-button">
            <Plus className="w-4 h-4 mr-2" /> Add Item
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Package className="w-10 h-10 mx-auto mb-2 text-slate-400" />
            <p>No items listed yet</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Qty</TableHead>
                <TableHead className="text-right">Est. Cost</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Location</TableHead>
                {isAdmin && <TableHead>Requested By</TableHead>}
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="purchase-items-table">
              {items.map((it, idx) => (
                <TableRow key={it.id} data-testid={`purchase-item-${idx}`}>
                  <TableCell>
                    <div className="font-medium">{it.item_name}</div>
                    {it.reason && <div className="text-xs text-slate-500 max-w-xs truncate">{it.reason}</div>}
                  </TableCell>
                  <TableCell><div className="flex items-center gap-1 capitalize">{categoryIcon(it.category)}{it.category}</div></TableCell>
                  <TableCell>{it.quantity}</TableCell>
                  <TableCell className="text-right">{it.estimated_cost != null ? `RM ${Number(it.estimated_cost).toFixed(2)}` : '—'}</TableCell>
                  <TableCell>{priorityBadge(it.priority)}</TableCell>
                  <TableCell className="text-sm text-slate-600">{it.location || '—'}</TableCell>
                  {isAdmin && <TableCell className="text-sm text-slate-600">{it.user_name}</TableCell>}
                  <TableCell>
                    {isAdmin ? (
                      <select
                        className="text-xs rounded-md border border-slate-300 h-8 px-2"
                        value={it.admin_status || 'pending'}
                        onChange={(e) => updateStatus(it.id, e.target.value)}
                        data-testid={`purchase-status-select-${idx}`}
                      >
                        <option value="pending">Pending</option>
                        <option value="purchased">Purchased</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    ) : statusBadge(it.admin_status)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {(it.user_id === user?.id) && (
                        <Button size="sm" variant="outline" onClick={() => openEdit(it)} data-testid={`edit-purchase-${idx}`}><Edit2 className="w-4 h-4" /></Button>
                      )}
                      {(isAdmin || it.user_id === user?.id) && (
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteItem(it.id)} data-testid={`delete-purchase-${idx}`}><Trash2 className="w-4 h-4" /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="purchase-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{editing ? 'Edit' : 'Add'} Item</DialogTitle>
            <DialogDescription>Purchase, restock, or repair request</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Item Name</Label><Input value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })} placeholder="e.g., A4 Printing Paper" data-testid="item-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="item-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="purchase">Purchase (new)</SelectItem>
                    <SelectItem value="restock">Restock</SelectItem>
                    <SelectItem value="repair">Repair</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="item-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Quantity</Label><Input type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="item-quantity-input" /></div>
              <div><Label>Estimated Cost (RM)</Label><Input type="number" step="0.01" value={form.estimated_cost} onChange={(e) => setForm({ ...form, estimated_cost: e.target.value })} data-testid="item-cost-input" /></div>
            </div>
            <div><Label>Location / Room</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g., Classroom 2A" data-testid="item-location-input" /></div>
            <div><Label>Reason / Description</Label><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={2} placeholder="Why is this needed?" data-testid="item-reason-input" /></div>
            <div><Label>Additional Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="item-notes-input" /></div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={saveItem} data-testid="save-purchase-button">
                {editing ? 'Update' : 'Add Item'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default PurchaseItemsSection;
